import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthState, User, LoginCredentials, Company } from '../types/auth';
import { getData, postData } from '../services/apiService';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<{ user: User; token: string | null; suggestTwoFactorSetup?: boolean } | { passwordExpired: boolean; userId: any; message: string } | { requiresTwoFactor: boolean; userId: any; message: string } | { requiresTwoFactorSetup: boolean; userId: any; message: string; user: User }>;
  logout: () => void;
  selectCompany: (company: Company) => void;
  switchCompany: (company: Company) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string | null } }
  | { type: 'LOGOUT' }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'SELECT_COMPANY'; payload: Company }
  | { type: 'SWITCH_COMPANY'; payload: Company };

const getInitialState = (): AuthState => {
  const userStr = localStorage.getItem('user');
  const selectedCompanyStr = localStorage.getItem('selectedCompany');
  let user = null;
  let selectedCompany = null;
  
  try {
    if (userStr) {
      const parsedUser = JSON.parse(userStr);
      if (parsedUser && parsedUser.email && parsedUser.role) {
        user = {
          ...parsedUser,
          role: parsedUser.role.toLowerCase(),
          companies: parsedUser.companies || []
        };
      }
    }
    
    if (selectedCompanyStr) {
      selectedCompany = JSON.parse(selectedCompanyStr);
    }
  } catch (error) {
    console.error('Error parsing user data:', error);
  }
  
  return {
    user,
    selectedCompany,
    isAuthenticated: !!user, // Base authentication on user presence, not token
    token: null, // Token is now managed by cookies
    loading: false,
    error: null
  };
};

const initialState: AuthState = getInitialState();

const hydrateAuth = async () => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
        try {
        const response = await getData('/api/auth/verify');
        if (response.status === "success" && response.message === "Authentication valid") {
                if (response.user && response.user.email && response.user.role) {
                        // Store the user data properly as JSON string
                        localStorage.setItem('user', JSON.stringify(response.user));
                        return true;
                }
        }
        return false;
        } catch (error) {
        console.error('Auth hydration error:', error);
                return false;
        }
  }
}

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: null, // Token is managed by cookies now
        isAuthenticated: true,
        loading: false,
        error: null,
        selectedCompany: null // Will be set after company selection
      };
    case 'SELECT_COMPANY':
      return {
        ...state,
        selectedCompany: action.payload
      };
    case 'SWITCH_COMPANY':
      return {
        ...state,
        selectedCompany: action.payload
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        selectedCompany: null,
        isAuthenticated: false,
        loading: false,
        error: null
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        selectedCompany: null,
        isAuthenticated: false,
        loading: false,
        error: action.payload
      };
    default:
      return state;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();

  // Handle initial auth state once on mount
  useEffect(() => {
    let mounted = true;
    
    const initAuth = async () => {
      const isValid = await hydrateAuth();
      if (!mounted) return;

      const userStr = localStorage.getItem('user');
      const selectedCompanyStr = localStorage.getItem('selectedCompany');

      if (isValid && userStr) {
        try {
          const parsedUser = JSON.parse(userStr);
          if (parsedUser && parsedUser.email && parsedUser.role) {
            const user = {
              ...parsedUser,
              role: parsedUser.role.toLowerCase(),
              companies: parsedUser.companies || []
            };
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: { user, token: null } // Token is now managed by cookies
            });

            // If selectedCompany exists and is still valid, restore it
            if (selectedCompanyStr) {
              try {
                const selectedCompany = JSON.parse(selectedCompanyStr);
                // Verify company is still accessible
                if (user.companies.find((c: Company) => c.id === selectedCompany.id)) {
                  dispatch({
                    type: 'SELECT_COMPANY',
                    payload: selectedCompany
                  });
                }
              } catch (error) {
                console.error('Error parsing selected company:', error);
                localStorage.removeItem('selectedCompany');
              }
            }
          } else {
            localStorage.clear();
            dispatch({ type: 'LOGOUT' });
          }
        } catch (error) {
          console.error('Error parsing auth data:', error);
          localStorage.clear();
          dispatch({ type: 'LOGOUT' });
        }
      } else {
        localStorage.clear();
        dispatch({ type: 'LOGOUT' });
      }
    };

    initAuth();

    return () => {
      mounted = false;
    };
  }, []); // Run only once on mount

  // Handle auth expiration events from apiService
  useEffect(() => {
    const handleAuthExpired = (event: CustomEvent) => {
      const { redirectTo } = event.detail;
      navigate(redirectTo, { replace: true });
    };

    window.addEventListener('auth-expired', handleAuthExpired as EventListener);

    return () => {
      window.removeEventListener('auth-expired', handleAuthExpired as EventListener);
    };
  }, [navigate]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    try {
      // Prepare request payload - only include twoFactorToken if it has a value
      const requestPayload: any = {
        email: credentials.email,
        password: credentials.password
      };
      
      if (credentials.twoFactorToken && credentials.twoFactorToken.trim() !== '') {
        requestPayload.twoFactorToken = credentials.twoFactorToken;
      }
      
      if (credentials.skipTwoFactorSetup) {
        requestPayload.skipTwoFactorSetup = credentials.skipTwoFactorSetup;
      }
      
      // Use our apiService to make the login request
      const data = await postData('/api/auth/login', requestPayload);
      
      // Check for password expired case FIRST (before checking general error status)
      if (data.passwordExpired) {
        return {
          passwordExpired: true,
          userId: data.userId,
          message: data.message
        };
      }
      
      // Check for 2FA requirement case
      if (data.requiresTwoFactor) {
        return {
          requiresTwoFactor: true,
          userId: data.userId,
          message: data.message
        };
      }
      
      // Check for 2FA setup requirement case
      if (data.requiresTwoFactorSetup) {
        return {
          requiresTwoFactorSetup: true,
          userId: data.userId,
          message: data.message,
          user: data.user // Include user data for client to handle
        };
      }
      
      if (data.status === 'error') {
        dispatch({ 
          type: 'LOGIN_FAILURE', 
          payload: data.message || 'Login failed'
        });
        throw new Error(data.message);
      }

      if (!data.data || !data.data.user) {
        dispatch({ 
          type: 'LOGIN_FAILURE', 
          payload: 'Invalid response from server'
        });
        throw new Error('Invalid response from server');
      }

      // Transform role to lowercase before storing
      const user = {
        ...data.data.user,
        role: data.data.user.role.toLowerCase(),
        companies: data.data.user.companies || [],
        isSSO: false // Mark as traditional login
      };
      
      // Store user data (token is now managed by HTTP-only cookies)
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('authMethod', 'traditional'); // Track auth method
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token: null }, // Token is managed by cookies
      });

      return { 
        user, 
        token: null, // Token is managed by cookies
        suggestTwoFactorSetup: data.data.suggestTwoFactorSetup || false
      };
    } catch (error) {
      throw error;
    }
  }, []);

  const selectCompany = useCallback((company: Company) => {
    localStorage.setItem('selectedCompany', JSON.stringify(company));
    dispatch({
      type: 'SELECT_COMPANY',
      payload: company
    });
  }, []);

  const switchCompany = useCallback((company: Company) => {
    localStorage.setItem('selectedCompany', JSON.stringify(company));
    dispatch({
      type: 'SWITCH_COMPANY',
      payload: company
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call server logout to clear the cookie
      await postData('/api/auth/logout', {});
      sessionStorage.clear();
    } catch (error) {
      console.error('Server logout failed:', error);
      // Continue with client-side logout even if server call fails
    }
    
    // Clear all storage
    localStorage.clear();
    
    // Reset auth state
    dispatch({ type: 'LOGOUT' });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        selectCompany,
        switchCompany,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
